import { useCallback, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { EmptyState } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { QueryStates } from "app/pages/sales-intelligence/common-components/QueryStates/QueryStates";
import { DeletePlaybookModal } from "app/pages/sales-intelligence/modules/playbooks/components/DeletePlaybookModal/DeletePlaybookModal";
import { EditSharedPlaybookModal } from "app/pages/sales-intelligence/modules/playbooks/components/EditSharedPlaybookModal/EditSharedPlaybookModal";
import type { PlaybooksColumnHandlers } from "app/pages/sales-intelligence/modules/playbooks/components/PlaybooksTable/columns";
import { PlaybooksTable } from "app/pages/sales-intelligence/modules/playbooks/components/PlaybooksTable/PlaybooksTable";
import { usePlaybooksSalesloftConnection } from "app/pages/sales-intelligence/modules/playbooks/hooks/usePlaybooksSalesloftConnection";
import { usePlaybooksTrackingService } from "app/pages/sales-intelligence/modules/playbooks/hooks/usePlaybooksTrackingService";
import { usePlaybookToasts } from "app/pages/sales-intelligence/modules/playbooks/hooks/usePlaybookToasts";
import { useDeletePlaybookMutation } from "app/pages/sales-intelligence/modules/playbooks/queries/useDeletePlaybookMutation";
import { usePlaybooksListQuery } from "app/pages/sales-intelligence/modules/playbooks/queries/usePlaybooksListQuery";
import type { Playbook } from "app/pages/sales-intelligence/modules/playbooks/types/playbooks.types";
import {
    countPlaybooksBySegment,
    splitPlaybooks,
} from "app/pages/sales-intelligence/modules/playbooks/utils/splitPlaybooks";
import usePrefetchTechnologiesDescription from "app/pages/sales-intelligence/modules/technology/hooks/usePrefetchTechnologiesDescription";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import { sentryErrorTrackerWithScope } from "app/pages/sales-intelligence/utils/tracking/sentry";

import { PlaybooksNewButton } from "./PlaybooksNewButton";
import { PlaybooksPageToolbar, type PlaybooksSegment } from "./PlaybooksPageToolbar";
import { StyledPlaybooksPageContainer } from "./styles";

export const PlaybooksPageContainer = () => {
    usePrefetchTechnologiesDescription();

    const { translate } = useTranslate();
    const { showSuccess, showError } = usePlaybookToasts();
    const playbooksTracking = usePlaybooksTrackingService();

    const canManageShared = salesSettingsHelper.isHighLevelAccount();

    const [segment, setSegment] = useState<PlaybooksSegment>("all");
    const [searchTerm, setSearchTerm] = useState("");
    const [playbookToDelete, setPlaybookToDelete] = useState<Playbook | undefined>(undefined);
    const [playbookToEdit, setPlaybookToEdit] = useState<Playbook | undefined>(undefined);

    const { data: playbooks, isLoading, isError, refetch } = usePlaybooksListQuery();
    const { mutateAsync: deletePlaybook, isPending: isDeleting } = useDeletePlaybookMutation();
    const hasUnresolvedCadence = useMemo(
        () => playbooks?.some((playbook) => playbook.cadence === null) ?? false,
        [playbooks],
    );
    const { isReconnectRequired: isSalesloftReconnectRequired, connectSalesloft } = usePlaybooksSalesloftConnection({
        enabled: hasUnresolvedCadence,
    });

    const { sharedCount, privateCount } = useMemo(
        () => (playbooks ? countPlaybooksBySegment(playbooks) : { sharedCount: 0, privateCount: 0 }),
        [playbooks],
    );

    const { sharedRows, privateRows } = useMemo(
        () => splitPlaybooks(playbooks ?? [], searchTerm),
        [playbooks, searchTerm],
    );

    const showShared = (segment === "all" || segment === "shared") && sharedRows.length > 0;
    const showPrivate = (segment === "all" || segment === "private") && privateRows.length > 0;

    const handleNewPlaybook = useCallback(
        (isSharedWithAccount: boolean) => {
            playbooksTracking.trackNewPlaybook();
            playbooksTracking.trackVisibilityChange(isSharedWithAccount);
            void siNavigator.goToPlaybookCreatePage({ isSharedWithAccount });
        },
        [playbooksTracking],
    );

    const handlers = useMemo<PlaybooksColumnHandlers>(
        () => ({
            onEdit: (playbook) => {
                if (playbook.isSharedWithAccount) {
                    setPlaybookToEdit(playbook);
                    return;
                }
                playbooksTracking.trackEditPlaybook(false);
                void siNavigator.goToPlaybookEditPage({ id: playbook.playbookId });
            },
            onDelete: (playbook) => {
                setPlaybookToDelete(playbook);
            },
            onConnectSalesloft: connectSalesloft,
        }),
        [playbooksTracking, connectSalesloft],
    );

    const handleConfirmEdit = () => {
        if (!playbookToEdit) {
            return;
        }
        playbooksTracking.trackEditPlaybook(true);
        void siNavigator.goToPlaybookEditPage({ id: playbookToEdit.playbookId });
        setPlaybookToEdit(undefined);
    };

    const handleConfirmDelete = async () => {
        if (!playbookToDelete) {
            return;
        }
        playbooksTracking.trackDeletePlaybookConfirm();
        try {
            await deletePlaybook(playbookToDelete.playbookId);
            setPlaybookToDelete(undefined);
            showSuccess("si.playbooks.delete.toast.success");
        } catch (error) {
            sentryErrorTrackerWithScope({
                errorMessage: "Failed to delete playbook",
                e: error,
                tags: { component: "playbooks" },
                params: { extra: { playbookId: playbookToDelete.playbookId } },
            });
            showError();
        }
    };

    const handleDeleteSelected = async (ids: number[]) => {
        if (ids.length === 0) {
            return;
        }
        playbooksTracking.trackBulkDelete(ids.length);
        try {
            await Promise.all(ids.map((playbookId) => deletePlaybook(playbookId)));
            showSuccess("si.playbooks.delete.toast.success");
        } catch (error) {
            sentryErrorTrackerWithScope({
                errorMessage: "Failed to delete selected playbooks",
                e: error,
                tags: { component: "playbooks" },
                params: { extra: { playbookIds: ids } },
            });
            showError();
            throw error; // let the table keep its selection when the delete fails
        }
    };

    const renderEmptyStateCta = useCallback(
        () => (
            <PlaybooksNewButton
                canCreateShared={canManageShared}
                dataAutomation="playbooks-empty-new-button"
                onNewPlaybook={handleNewPlaybook}
            />
        ),
        [canManageShared, handleNewPlaybook],
    );

    const isEmpty = !isLoading && !isError && playbooks?.length === 0;

    return (
        <StyledPlaybooksPageContainer
            data-automation="playbooks-page-container"
            gap="30px"
            margin="0 auto 40px"
            padding="40px 0"
            width="100%"
        >
            <Stack
                flex="0 0 auto"
                gap="12px"
                margin="10px 0"
            >
                <Text
                    fontVariant="h2"
                    color={colorsPalettes.carbon["400"]}
                >
                    {translate("si.playbooks.header")}
                </Text>
                <Text
                    fontVariant="body1"
                    color={colorsPalettes.carbon["400"]}
                >
                    {translate("si.playbooks.header.subtitle")}
                </Text>
            </Stack>

            <PlaybooksPageToolbar
                canCreateShared={canManageShared}
                sharedCount={sharedCount}
                privateCount={privateCount}
                onSegmentChange={(next) => {
                    playbooksTracking.trackSegmentChange(next);
                    setSegment(next);
                }}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onNewPlaybook={handleNewPlaybook}
            />

            <QueryStates
                isLoading={isLoading}
                isError={isError}
                onRetry={() => {
                    playbooksTracking.trackListRetry();
                    void refetch();
                }}
                errorDataAutomation="playbooks-list-error"
                errorRetryDataAutomation="playbooks-list-error-retry"
            />

            {!isLoading && !isError ? (
                isEmpty ? (
                    <Flex
                        flex="1"
                        justifyContent="center"
                        width="100%"
                        data-automation="playbooks-empty-state"
                        margin="30px auto"
                    >
                        <EmptyState
                            title={translate("si.playbooks.empty.title")}
                            subtitle={translate("si.playbooks.empty.subtitle")}
                            CtaButtons={renderEmptyStateCta}
                        />
                    </Flex>
                ) : (
                    <Stack gap="30px">
                        {showShared ? (
                            <PlaybooksTable
                                rows={sharedRows}
                                handlers={handlers}
                                dataAutomation="playbooks-table-shared"
                                title={translate("si.playbooks.section.shared.title")}
                                subtitle={translate("si.playbooks.section.shared.subtitle")}
                                selectable={canManageShared}
                                isSalesloftReconnectRequired={isSalesloftReconnectRequired}
                                onDeleteSelected={handleDeleteSelected}
                            />
                        ) : null}

                        {showPrivate ? (
                            <PlaybooksTable
                                rows={privateRows}
                                handlers={handlers}
                                dataAutomation="playbooks-table-private"
                                title={translate("si.playbooks.section.private.title")}
                                subtitle={translate("si.playbooks.section.private.subtitle")}
                                selectable
                                isSalesloftReconnectRequired={isSalesloftReconnectRequired}
                                onDeleteSelected={handleDeleteSelected}
                            />
                        ) : null}
                    </Stack>
                )
            ) : null}

            <EditSharedPlaybookModal
                isOpen={playbookToEdit !== undefined}
                onCancel={() => setPlaybookToEdit(undefined)}
                onConfirm={handleConfirmEdit}
            />

            <DeletePlaybookModal
                isOpen={playbookToDelete !== undefined}
                isShared={playbookToDelete?.isSharedWithAccount ?? false}
                playbookName={playbookToDelete?.name ?? ""}
                isDeleting={isDeleting}
                onCancel={() => setPlaybookToDelete(undefined)}
                onConfirm={handleConfirmDelete}
            />
        </StyledPlaybooksPageContainer>
    );
};
